import { email, maxLength, required, useTranslate } from 'react-admin';
import React from 'react';
import { Box, Typography } from '@material-ui/core';
import CurrenciesReference from '../../../../base/components/reference-fields/currencies';
import FeaturesInput from '../../../brand/components/BrandForm/FeaturesInput';
import { BooleanInput, TextInput } from '../../../../base/components/ra/inputs';
import NumberInput from '../../../../base/components/ra/inputs/integer.input';
import RoyaltyRateInput from './RoyaltyRateInput';
import CompanyAddressInput from './CompanyAddressInput';
import { useHasCreateInvoicePermission, useHasReadCurrencyPermission, useHasReadInvoicePermission } from '../utils';
import AmountInput from '../../../../base/components/ra/inputs/AmountInput';
import DatePickerInput from '../../../../base/components/ra/inputs/datePicker.input';
import { MONTH_DISPLAY_FORMAT } from '../../../../constant';

const GroupForm = ({ isEdit }) => {
  const t = useTranslate();
  const hasCreateInvoicePermission = useHasCreateInvoicePermission();
  const hasReadInvoicePermission = useHasReadInvoicePermission();
  const hasReadCurrencyPermission = useHasReadCurrencyPermission();

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        gap: 12,
      }}
    >
      {/* ------ General Info ------ */}
      <Box display="flex" flexDirection="column">
        <Typography variant="h4" gutterBottom>
          {t('resources.group.generalInfo')}
        </Typography>
        <TextInput
          source="name"
          label="resources.group.fields.name"
          validate={[
            required(t('ra.validation.required')),
            maxLength(
              55,
              t('ra.validation.maxLength', {
                max: 55,
              }),
            ),
          ]}
        />
        <TextInput
          source="managerEmail"
          label="resources.group.fields.managerEmail"
          validate={[email(t('ra.validation.email')), required(t('ra.validation.required'))]}
        />
        <FeaturesInput label="resources.feature-flag.name" source="features" />
        {hasReadCurrencyPermission && (
          <CurrenciesReference source="currencies" validate={required(t('ra.validation.required'))} />
        )}
        <TextInput source="desc" multiline rows={5} label="resources.group.fields.desc" />
      </Box>

      {/* ------ General Info ------ */}
      {hasReadInvoicePermission && (
        <Box display="flex" flexDirection="column">
          <Typography variant="h4" gutterBottom>
            {t('resources.group.invoiceSettings')}
          </Typography>
          <RoyaltyRateInput disabled={!hasCreateInvoicePermission} />
          <NumberInput
            source="invoiceProfile.paymentTermDays"
            label="resources.group.fields.paymentTerms"
            disabled={!hasCreateInvoicePermission}
          />
          <TextInput
            source="invoiceProfile.companyName"
            label="resources.group.fields.companyName"
            disabled={!hasCreateInvoicePermission}
            validate={[
              maxLength(
                255,
                t('ra.validation.maxLength', {
                  max: 255,
                }),
              ),
            ]}
          />
          <CompanyAddressInput disabled={!hasCreateInvoicePermission} />
          <TextInput
            source="invoiceProfile.city"
            label="resources.group.fields.city"
            validate={[
              maxLength(
                128,
                t('ra.validation.maxLength', {
                  max: 128,
                }),
              ),
            ]}
            disabled={!hasCreateInvoicePermission}
          />
          <TextInput
            source="invoiceProfile.country"
            label="resources.group.fields.country"
            disabled={!hasCreateInvoicePermission}
            validate={[
              maxLength(
                64,
                t('ra.validation.maxLength', {
                  max: 255,
                }),
              ),
            ]}
          />
          <TextInput
            source="invoiceProfile.regNo"
            label="resources.group.fields.regNo"
            disabled={!hasCreateInvoicePermission}
            validate={[
              maxLength(
                24,
                t('ra.validation.maxLength', {
                  max: 24,
                }),
              ),
            ]}
          />
          {isEdit && (
            <Box
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                md: 'repeat(2, 1fr)',
              }}
              alignItems="flex-end"
              sx={{
                gap: 16,
              }}
            >
              <AmountInput
                source="openingBalance"
                label={t('resources.group.fields.openingBalance')}
                disabled={!hasCreateInvoicePermission}
                prefix="USD "
                allowNegative
                isRequired={false}
              />
              <DatePickerInput
                allowClear={true}
                isRequired={false}
                label={t('resources.group.fields.openingBalanceTime')}
                timeFormat=""
                source="openingBalanceStartTime"
                showTime={false}
                disabled={!hasCreateInvoicePermission}
                picker="month"
                formatTime={MONTH_DISPLAY_FORMAT}
              />
            </Box>
          )}
        </Box>
      )}
      <BooleanInput label="resources.group.fields.enabled" source="enabled" />
    </Box>
  );
};

export default GroupForm;
